import React, { useCallback, useContext, useMemo } from 'react';
import GroupFormComponent from '../../form/GroupFormComponent/GroupFormComponent';
import { sendFilterFunc } from '../utils';
import T from '../../../common/components/T';
import UserContext from '../../../common/utils/getContext';
import classnames from 'classnames';
import { generateCompStylers, getStylePropsFunc } from '../../../common/utils/styles';
import { useRouter } from 'next/router';

const PortalFilterGroupForm = ({
    formData,
    setFormData,
    compStyles,
    dataFiltersToUrl,
    dataFiltersItemsCountToUrl,
    setGridData,
    filter,
    sort,
    setUrl,
    convertData,
    convertHeadData,
    token,
    setShowLoading,
    setShowModal,
    inputGlobFilter,
    grid,
    resetFilter
}) => {
    const { userToken, setAlertData, lang } = useContext(UserContext);
    const { data, getFormStyle, formDataMain, setFormDataMain } = filter;
    const router = useRouter();
    const setFormDataMainFunc = useCallback(
        (key, val) => {
            setFormDataMain && setFormDataMain(key, val);
            setFormData((pre) => ({ ...pre, [key]: val }));
        },
        [setFormData, setFormDataMain]
    );
    const filterData = data(formData);

    const userFormStyles = useMemo(
        () => (getFormStyle ? getFormStyle(compStyles, formData) : {}),
        [compStyles, formData, getFormStyle]
    );
    const formStyles = useMemo(() => {
        const { stylers, classes } = generateCompStylers(compStyles);
        const accum = {};
        Object.keys(filterData)?.forEach((key) => {
            filterData[key]?.fields?.forEach((field) => {
                const { stylersField, classesField } = generateCompStylers(userFormStyles[field?.attribute], 'Field');

                accum[field?.attribute] = {
                    ...userFormStyles[field?.attribute],
                    wrapper: {
                        className: classnames(
                            classes.formControlWrap,
                            ['checkbox-list', 'radio-list'].includes(field?.type) && classes.formControlAlignStart,
                            ['checkbox', 'checkbox-switcher', 'radio-switcher'].includes(field?.type) &&
                                classes.formControlAlignCenter,
                            classesField.wrapper
                        ),
                        ...stylers.formControlWrap,
                        ...(['checkbox-list', 'radio-list'].includes(field?.type) ? stylers.formControlAlignStart : {}),
                        ...(['checkbox', 'checkbox-switcher', 'radio-switcher'].includes(field?.type)
                            ? stylers.formControlAlignCenter
                            : {}),
                        ...stylersField.wrapper
                    }
                };
            });
        });
        return accum;
    }, [compStyles, filterData, userFormStyles]);
    const filterFuncs = useMemo(
        () =>
            filter?.funcs
                ? filter.funcs(resetFilter, setFormDataMainFunc)
                : {
                      cancelFuncs: {
                          web: { onClick: async () => await resetFilter() }
                      }
                  },
        [filter, resetFilter, setFormDataMainFunc]
    );
    const sendFilter = useCallback(
        (groupFormData, setAllowedSend, setGroupFormDataError) => {
            const setDataFiltersToUrl = (data) => {
                if (filter?.setFiltersToParent) {
                    filter?.setFiltersToParent(data);
                }
                if (dataFiltersToUrl) {
                    dataFiltersToUrl.current = data;
                }
            };
            const text = groupFormData?.q ? groupFormData?.q : '';
            const setData = (grid_data, clear_data) => {
                if (grid?.setClearFilterResults) {
                    grid?.setClearFilterResults(clear_data, text);
                }
                if (grid?.setInitData) {
                    grid.setInitData(grid_data);
                } else {
                    setGridData(grid_data);
                }
            };

            sendFilterFunc(
                groupFormData,
                filterData,
                setAllowedSend,
                setGroupFormDataError,
                setAlertData,
                dataFiltersItemsCountToUrl,
                setData,
                convertData,
                userToken,
                setDataFiltersToUrl,
                lang,
                filter || null,
                convertHeadData,
                setUrl,
                token,
                setShowLoading,
                inputGlobFilter,
                setShowModal,
                sort,
                router
            );
        },
        [
            convertData,
            convertHeadData,
            dataFiltersItemsCountToUrl,
            dataFiltersToUrl,
            filter,
            filterData,
            grid,
            inputGlobFilter,
            lang,
            setAlertData,
            setGridData,
            setShowLoading,
            setShowModal,
            setUrl,
            token,
            userToken,
            sort,
            router
        ]
    );
    const filterDataWithValue = Object.keys(filterData).reduce(
        (acc, curr) => ({
            ...acc,
            [curr]: {
                ...filterData[curr],
                fields: filterData[curr].fields?.map((field) => {
                    if (field?.type === 'range-values') {
                        const value = field?.options?.reduce((acc, opt) => {
                            if (formData[opt] !== undefined) {
                                return { ...acc, [opt]: formData[opt] };
                            }
                            return acc;
                        }, {});
                        if (value && Object.keys(value).length) {
                            return { ...field, value };
                        }
                    }
                    if (formData[field?.attribute] !== undefined || (formDataMain && formDataMain[field?.attribute])) {
                        if (['select', 'dropdown', 'location_widget'].includes(field.type)) {
                            const val = formData[field?.attribute] || (formDataMain && formDataMain[field?.attribute]);
                            return {
                                ...field,
                                ...(typeof val === 'object' ? { value: val } : { valueId: val })
                            };
                        }
                        if (field.type === 'dropdown-multiselect') {
                            const val = formData[field?.attribute] || (formDataMain && formDataMain[field?.attribute]);
                            return {
                                ...field,
                                value: Array.isArray(val)
                                    ? val
                                    : typeof val === 'string'
                                    ? val?.split(',')?.map((el) => ({ id: el }))
                                    : []
                            };
                        }
                        return {
                            ...field,
                            value: formData[field?.attribute] || (formDataMain && formDataMain[field?.attribute])
                        };
                    }

                    return field;
                })
            }
        }),
        {}
    );
    const getStyleProps = (...keys) => getStylePropsFunc(compStyles, ...keys);

    return (
        <div {...getStyleProps('adminFilterWindowContentWrapper')}>
            <GroupFormComponent
                data={filterDataWithValue}
                formStyles={formStyles}
                formDataMain={formDataMain || formData}
                setFormDataMain={setFormDataMainFunc}
                sendData={{ sender: sendFilter }}
                customStyles={compStyles}
                customData={{
                    formButtonBlockWrapper: compStyles.formButtonBlockWrapper,
                    fieldsWrapper: compStyles.formGlobWrap,
                    customFormButton: {
                        ok: {
                            singleButton: classnames(compStyles.buttonsWidth, compStyles.singleButtonOk),
                            butWrapper: classnames(compStyles.butWrapperOk)
                        },
                        cancel: {
                            singleButton: classnames(compStyles.buttonsWidth, compStyles.singleButtonCancel),
                            butWrapper: classnames(compStyles.butWrapperOkCancel)
                        }
                    },
                    ...(filter?.customData ? filter?.customData(formData) : {})
                }}
                buttonText={filter.buttonText || <T textName='apply' defaultStr='Применить' page='all' />}
                cancelText={filter.cancelText || <T textName='reset' defaultStr='Сбросить' page='all' />}
                funcs={filterFuncs}
            />
        </div>
    );
};
export default PortalFilterGroupForm;
